import type { ReactNode } from 'react';
import { Button, SetupHeading } from '@forecastify/ui';
import { useT } from '../../i18n';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Setup step 4 — all set: what was made, and the way in.                    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The onboarding mockup's step 4: the logo with the face over its corner, a sentence, the address
 * and the invitations as two rows, and one button.
 *
 * ⚠️ **The mockup's *"a short guided tour will help you find your way around"* is drawn now** that
 * the onboarding branch has brought the tour: the welcome cards on the dashboard this button opens
 * offer it (`welcome-cards.tsx`, `canTour`).
 */
export function DoneStep({
  workspaceName,
  address,
  logoSrc,
  avatarSrc,
  workspaceInitial,
  personInitials,
  sent,
  pending,
  entering,
  onEnter,
  onBack,
}: {
  readonly workspaceName: string;
  /** As it is shown — `app.forecastify.hu/kovacs-digital`. */
  readonly address: string;
  readonly logoSrc: string | null;
  readonly avatarSrc: string | null;
  readonly workspaceInitial: string;
  readonly personInitials: string;
  /** Invitations that went on an earlier *Belépés* that stopped at a failure. */
  readonly sent: number;
  /** Invitations *Belépés* will send — nothing is sent before it (14 Oct 2026). */
  readonly pending: number;
  readonly entering: boolean;
  readonly onEnter: () => void;
  /** Back to step 3 — invitations sent stay sent, and are listed there. */
  readonly onBack: () => void;
}): ReactNode {
  const t = useT();
  const rows = [
    { label: t('setup.done.address'), value: address, muted: false },
    {
      label: t('setup.done.invites'),
      value:
        pending > 0
          ? t('setup.done.pending', { n: pending })
          : sent > 0
            ? t('setup.done.sent', { n: sent })
            : t('setup.done.none'),
      muted: pending === 0 && sent === 0,
    },
  ];

  return (
    <>
      <div className="flex items-center max-[899px]:justify-center">
        <div className="size-14 overflow-hidden rounded-[12px]">
          {logoSrc === null ? (
            <div className="bg-primary text-primary-foreground grid size-14 place-items-center text-[22px] font-semibold">
              {workspaceInitial}
            </div>
          ) : (
            <img src={logoSrc} alt="" className="size-14 object-cover" />
          )}
        </div>
        <div className="ring-background -ml-3 size-14 overflow-hidden rounded-full ring-4">
          {avatarSrc === null ? (
            <div className="bg-foreground text-background grid size-14 place-items-center text-[18px] font-semibold">
              {personInitials}
            </div>
          ) : (
            <img src={avatarSrc} alt="" className="size-14 object-cover" />
          )}
        </div>
      </div>
      <SetupHeading
        title={t('setup.done.title')}
        subtitle={t('setup.done.subtitle', { ws: workspaceName })}
      />
      <div className="flex flex-col rounded-lg border">
        {rows.map((row) => (
          <div
            key={row.label}
            className="flex items-baseline gap-3 border-b px-3.5 py-2.5 last:border-0"
          >
            <span className="text-muted-foreground w-[96px] shrink-0 text-[12.5px]">
              {row.label}
            </span>
            <span
              className={
                row.muted
                  ? 'text-muted-foreground min-w-0 flex-1 text-[13px] text-pretty'
                  : 'min-w-0 flex-1 text-[13px] text-pretty tabular-nums'
              }
            >
              {row.value}
            </span>
          </div>
        ))}
      </div>
      {/* The mockup's pairing: the sentence and the way in, 12px apart. */}
      <div className="flex flex-col gap-3">
        <p className="text-[13px] leading-[20px]">{t('setup.done.next')}</p>
        <Button
          type="button"
          autoFocus
          disabled={entering}
          className="h-10 w-full text-[13.5px]"
          onClick={onEnter}
        >
          {entering ? t('setup.done.entering') : t('setup.done.enter')}
        </Button>
      </div>
      <Button
        type="button"
        variant="ghost"
        disabled={entering}
        className="text-muted-foreground -mt-4 h-9 self-start text-[13px] max-[899px]:w-full min-[900px]:-ml-3"
        onClick={onBack}
      >
        {t('setup.back')}
      </Button>
    </>
  );
}
